import { AnalyticsExtensionsSettingsDialogComponent } from '@tableau/analytics-extensions-settings-dialog-ui';
import { ConnectionParams } from '@tableau/analytics-extensions-settings-dialog-ui/lib/src/components/AnalyticsExtensionsSettingsDialogComponent';
import { ScreenType } from '@tableau/analytics-extensions-settings-dialog-ui/lib/src/util/ScreenTypes';
import {
  InlineAlertType,
  InlineAlertWidget,
} from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../../codegen/Localize';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import {
  IAnalyticsExtensionsSettings
} from '../../controllers/SiteSettingsForAnalyticsExtensionsCtrl';
import {
  IAnalyticsConnectionBrief,
  IAnalyticsExtConnectionId,
  IAnalyticsExtConnectionSettings,
} from '../../types/ISiteSettingsForSiteAdmin';
import { AnalyticsExtensionsSettingsStyles } from '../styles/AnalyticsExtensionsSettingsStyles';

export interface IAnalyticsExtensionsDialogProps {
  isOnline: boolean;
  sendUpdateConnectionRequest: (connectionSettings: IAnalyticsExtConnectionSettings) => Promise<IResult>;
  sendDeleteRequest: (connectionId: IAnalyticsExtConnectionId) => Promise<IResult>;
  initialAnalyticsExtensionsSettings: IAnalyticsExtensionsSettings;
}

export interface DialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
  readonly connectionLUID: string;
  ctrlProps: IAnalyticsExtensionsDialogProps;
  readonly savedAccessTokensEnabled?: boolean;
}

export enum AnalyticsExtensionsErrorCode {
  BAD_REQUEST_ERROR = 'BAD-REQUEST-ERROR',
  CONFLICT_ERROR = 'CONFLICT-ERROR',
  CONNECTION_NAME_TOO_LONG = 'CONNECTION-NAME-TOO-LONG',
  ED_ALREADY_ADDED_ERROR = 'ED-ALREADY-ADDED-ERROR',
  ED_TOKEN_ERROR = 'ED-TOKEN-ERROR',
  HOSTNAME_TOO_LONG = 'HOSTNAME-TOO-LONG',
  PASSWORD_TOO_LONG = 'PASSWORD-TOO-LONG',
  SERVER_ERROR = 'SERVER-ERROR',
  USERNAME_TOO_LONG = 'USERNAME-TOO-LONG',
}

const PORT_START_VALUE: number = 443;
const CONFLICT_ERROR_CODE: number = 409;

const MAX_CONNECTION_NAME_LENGTH: number = 255;
const MAX_HOSTNAME_LENGTH: number = 255;
const MAX_USERNAME_LENGTH: number = 255;
const MAX_PASSWORD_LENGTH: number = 255;

const http: string = 'http://';
const https: string = 'https://';

export type AnalyticsExtensionsSettingsDialogProps =
  IAnalyticsExtensionsDialogProps & DialogProps;

export interface AnalyticsExtensionsSettingsForSiteState {
  readonly busy: boolean;
  readonly error: AnalyticsExtensionsErrorCode;
  isOnline: boolean;
  configSettings: IAnalyticsExtConnectionSettings;
  protocol: string;
}

export class AnalyticsExtensionsSettingsDialog extends React.Component<DialogProps, AnalyticsExtensionsSettingsForSiteState> {
  readonly errorStrings = {
    [AnalyticsExtensionsErrorCode.BAD_REQUEST_ERROR]: Messages.settingsUpdateFailure(),
    [AnalyticsExtensionsErrorCode.CONFLICT_ERROR]: Messages.settingsDuplicateConnectionNameFailure(),
    [AnalyticsExtensionsErrorCode.CONNECTION_NAME_TOO_LONG]: Messages.settingsConnectionNameTooLongFailure({ MAX_LENGTH: MAX_CONNECTION_NAME_LENGTH.toString() }),
    [AnalyticsExtensionsErrorCode.ED_ALREADY_ADDED_ERROR]: Messages.analyticsExtensionsEinsteinDiscoveryConnectionAlreadyOnSite(),
    [AnalyticsExtensionsErrorCode.ED_TOKEN_ERROR]: Messages.analyticsExtensionsNoEDOnSavedAccessTokensDisabled(),
    [AnalyticsExtensionsErrorCode.HOSTNAME_TOO_LONG]: Messages.settingsHostnameTooLongFailure({ MAX_LENGTH: MAX_HOSTNAME_LENGTH.toString() }),
    [AnalyticsExtensionsErrorCode.PASSWORD_TOO_LONG]: Messages.settingsPasswordTooLongFailure({ MAX_LENGTH: MAX_PASSWORD_LENGTH.toString() }),
    [AnalyticsExtensionsErrorCode.SERVER_ERROR]: Messages.settingsUpdateFailure(),
    [AnalyticsExtensionsErrorCode.USERNAME_TOO_LONG]: Messages.settingsUsernameTooLongFailure({ MAX_LENGTH: MAX_USERNAME_LENGTH.toString() })
  };

  readonly ConnectionTypes = {
    TABPY: {
      TAG: 'TABPY',
      DISPLAY: 'TabPy/' + Messages.analyticsExtension()
    },
    RSERVE: {
      TAG: 'RSERVE',
      DISPLAY: 'Rserve'
    },
    EINSTEIN: {
      TAG: 'EINSTEIN_DISCOVERY',
      DISPLAY: 'Einstein Discovery'
    },
    GENERIC_API: {
      TAG: 'GENERIC_API',
      DISPLAY: Messages.analyticsExtensionsAPI()
    },
    SELECT: Messages.select()
  };

  readonly defaultConnection: ConnectionParams = {
    host: '',
    port: PORT_START_VALUE,
    isAuthEnabled: false,
    isSecure: false,
    username: '',
    password: '',
    typeIndex: 0,
    connectionName: ''
  };

  readonly newConnectionBrief: IAnalyticsConnectionBrief = {
    connection_type: this.ConnectionTypes.SELECT,
    connection_name: ''
  };

  private originalSettings: IAnalyticsExtConnectionSettings;
  private initProtocol: string = this.initialProtocol();

  public constructor(props: DialogProps) {
    super(props);
    let connection: IAnalyticsExtConnectionSettings;
    connection = this.props.ctrlProps.initialAnalyticsExtensionsSettings.connectionSettingsList.find(c => c.connection_luid === props.connectionLUID);
    let brief = connection ? Object.assign({}, connection.connection_brief) : { ...this.newConnectionBrief };

    if (connection) {
      brief = {
        ...brief,
        connection_type: this.convertDisplayTypeToTag(brief.connection_type),
      };
    }
    this.originalSettings = {
      ...connection,
      connection_brief: brief,
      is_ssl_enabled: props.ctrlProps.isOnline ? props.ctrlProps.isOnline : connection?.is_ssl_enabled,
      is_auth_enabled: props.ctrlProps.isOnline ? props.ctrlProps.isOnline : connection?.is_auth_enabled
    };
    this.state = {
      busy: false,
      error: null,
      isOnline: props.ctrlProps.isOnline,
      configSettings: this.originalSettings,
      protocol: this.initProtocol
    };
  }

  private initialProtocol() {
    let retVal = '';
    let connection = this.props.ctrlProps.initialAnalyticsExtensionsSettings.connectionSettingsList?.find(connection => connection.connection_luid === this.props.connectionLUID);
    let connectionDisplayType = connection ? connection.connection_brief.connection_type : this.ConnectionTypes.SELECT;
    if (connectionDisplayType === this.ConnectionTypes.TABPY.DISPLAY) {
      const ssl = connection.is_ssl_enabled;
      retVal = ssl ? https : http;
    }
    return retVal;
  }

  public updateProtocol() {
    if (this.state.configSettings.connection_brief.connection_type === this.ConnectionTypes.RSERVE.TAG) {
      this.setState({ protocol: '' });
    } else if (this.state.configSettings.is_ssl_enabled && this.state.configSettings.connection_brief.connection_type === this.ConnectionTypes.TABPY.TAG) {
      this.setState({ protocol: https });
    } else if (!this.state.configSettings.is_ssl_enabled && this.state.configSettings.connection_brief.connection_type === this.ConnectionTypes.TABPY.TAG) {
      this.setState({ protocol: http });
    } else {
      // no connection type selected yet
      this.setState({ protocol: '' });
    }
  }

  public convertTypeDisplay(value: string): string {
    if (value === this.ConnectionTypes.RSERVE.TAG) {
      return this.ConnectionTypes.RSERVE.DISPLAY;
    } else if (value === this.ConnectionTypes.TABPY.TAG) {
      return this.ConnectionTypes.TABPY.DISPLAY;
    } else if (value === this.ConnectionTypes.EINSTEIN.TAG) {
      return this.ConnectionTypes.EINSTEIN.DISPLAY;
    } else if (value === this.ConnectionTypes.GENERIC_API.TAG) {
      return this.ConnectionTypes.GENERIC_API.DISPLAY;
    } else {
      return this.ConnectionTypes.SELECT;
    }
  }

  public convertTypeToScreen(value: string): ScreenType {
    switch (value) {
      case this.ConnectionTypes.RSERVE.TAG:
        return ScreenType.RServe;
      case this.ConnectionTypes.TABPY.TAG:
        return ScreenType.TabPy;
      case this.ConnectionTypes.EINSTEIN.TAG:
        return ScreenType.EinsteinDiscovery;
      case this.ConnectionTypes.GENERIC_API.TAG:
        return ScreenType.AnalyticsExtensionsAPI;
      default:
        return ScreenType.Menu;
    }
  }

  public convertDisplayTypeToTag(value: string): string {
    switch (value) {
      case this.ConnectionTypes.RSERVE.DISPLAY:
        return this.ConnectionTypes.RSERVE.TAG;
      case this.ConnectionTypes.TABPY.DISPLAY:
        return this.ConnectionTypes.TABPY.TAG;
      case this.ConnectionTypes.EINSTEIN.DISPLAY:
        return this.ConnectionTypes.EINSTEIN.TAG;
      case this.ConnectionTypes.GENERIC_API.DISPLAY:
        return this.ConnectionTypes.GENERIC_API.TAG;
      default:
        return this.ConnectionTypes.SELECT;
    }
  }

  private convertScreenToType(value: ScreenType): string {
    switch (value) {
      case ScreenType.RServe:
        return this.ConnectionTypes.RSERVE.DISPLAY;
      case ScreenType.TabPy:
        return this.ConnectionTypes.TABPY.DISPLAY;
      case ScreenType.EinsteinDiscovery:
        return this.ConnectionTypes.EINSTEIN.DISPLAY;
      case ScreenType.AnalyticsExtensionsAPI:
        return this.ConnectionTypes.GENERIC_API.DISPLAY;
      default:
        return '';
    }
  }

  public convertConnectionSettingToParams(value: IAnalyticsExtConnectionSettings): ConnectionParams {
    let params: ConnectionParams = this.defaultConnection;
    // It needs at least one defined parameter to try to send, and show an error for wrong parameters.
    params.host = value.host ?? params.host;
    params.isAuthEnabled = value.is_auth_enabled;
    params.isSecure = value.is_ssl_enabled;
    params.password = value.password;
    params.port = value.port ?? params.port;
    params.typeIndex = this.convertTypeToScreen(value.connection_brief.connection_type);
    params.username = value.username;
    params.connectionName = value.connection_brief.connection_name;
    return params;
  }

  private setErrorState(errorCode: AnalyticsExtensionsErrorCode): void {
    this.setState({
      error: errorCode
    });
  }

  private removeProtocolFromHostUrl() {
    let settings = this.state.configSettings;
    if (!settings.host) {
      return;
    }
    if (settings.host.indexOf('://') !== -1) {
      settings.host = settings.host.substr(settings.host.lastIndexOf('://') + 3);
      this.setState({ configSettings: settings });
    }
  }

  private validateInput(): boolean {
    const settings = this.state.configSettings;
    if (settings.connection_brief.connection_name.length > MAX_CONNECTION_NAME_LENGTH) {
      this.setErrorState(AnalyticsExtensionsErrorCode.CONNECTION_NAME_TOO_LONG);
      return false;
    } else if (settings.host.length > MAX_HOSTNAME_LENGTH) {
      this.setErrorState(AnalyticsExtensionsErrorCode.HOSTNAME_TOO_LONG);
      return false;
    } else if (settings.is_auth_enabled && settings.username.length > MAX_USERNAME_LENGTH) {
      this.setErrorState(AnalyticsExtensionsErrorCode.USERNAME_TOO_LONG);
      return false;
    } else if (settings.is_auth_enabled && settings.password.length > MAX_PASSWORD_LENGTH) {
      this.setErrorState(AnalyticsExtensionsErrorCode.PASSWORD_TOO_LONG);
      return false;
    }
    return true;
  }

  public handleChangeCallback = (value: any, fieldName: keyof IAnalyticsExtConnectionSettings) => {
    let updateSettings = this.state.configSettings;
    (updateSettings[fieldName] as any) = value;
    this.setState({ configSettings: updateSettings });
    this.updateProtocol();
    this.removeProtocolFromHostUrl();
  };

  public handleMetadataChangeCallback = (value: any, fieldName: keyof IAnalyticsConnectionBrief) => {
    let updateSettings = this.state.configSettings;
    let brief = this.state.configSettings.connection_brief;
    (brief[fieldName] as any) = value;
    updateSettings['connection_brief'] = brief;
    this.setState({ configSettings: updateSettings });
    this.updateProtocol();
    this.removeProtocolFromHostUrl();
  };

  public handleTypeChangeCallback = (value: any, fieldName: keyof IAnalyticsConnectionBrief) => {
    let updateSettings = this.state.configSettings;
    let brief = this.state.configSettings.connection_brief;
    let valueTAG = value;
    if (value === this.ConnectionTypes.RSERVE.DISPLAY) {
      valueTAG = this.ConnectionTypes.RSERVE.TAG;
    } else if (value === this.ConnectionTypes.TABPY.DISPLAY) {
      valueTAG = this.ConnectionTypes.TABPY.TAG;
    } else if (value === this.ConnectionTypes.EINSTEIN.DISPLAY) {
      valueTAG = this.ConnectionTypes.EINSTEIN.TAG;
    } else if (value === this.ConnectionTypes.GENERIC_API.DISPLAY) {
      valueTAG = this.ConnectionTypes.GENERIC_API.TAG;
    }
    (brief[fieldName] as any) = valueTAG;
    updateSettings['connection_brief'] = brief;
    this.setState({ configSettings: updateSettings });
    this.updateProtocol();
  };

  // TODO: This is a temporary solution to make Einstein_Discovery work with connection-name.
  // The right solution should be in the service side, to let the validation of an empty connection_name for ED pass.
  private handleConnectionName = (connectionType: string, connectionName?: string): string => {
    if (connectionType === this.ConnectionTypes.EINSTEIN.DISPLAY) {
      return 'default_ed';
    }
    return connectionName ? connectionName : '';
  };

  private handleCreate = () => {
    if (this.state.busy) {
      return;
    }
    this.props.ctrlProps.sendUpdateConnectionRequest(this.state.configSettings).then(
      (result: IResult) => {
        this.setState({ busy: false });
        if (!result || 'errors' in result) {
          const isConflict = result?.errors?.[0]?.httpErrorCode === CONFLICT_ERROR_CODE;
          const errorCode = isConflict ? AnalyticsExtensionsErrorCode.CONFLICT_ERROR : AnalyticsExtensionsErrorCode.BAD_REQUEST_ERROR;
          this.setErrorState(errorCode);
          return;
        } else {
          // must reassign this or risk mutating it later while displaying UI
          let updatedConnection = JSON.parse(JSON.stringify(result)) as IAnalyticsExtConnectionSettings;
          let connectionList = this.props.ctrlProps.initialAnalyticsExtensionsSettings.connectionSettingsList;
          let found = false;
          connectionList.forEach((connection: IAnalyticsExtConnectionSettings, index: number) => {
            if (connection.connection_luid === updatedConnection.connection_luid || connection.connection_luid === '') {
              found = true;
              connectionList[index] = updatedConnection;
            }
          });
          if (!found) {
            connectionList.push(updatedConnection);
          }
          this.props.close(result);
        }
      }
    );
  };

  private handleEinsteinDiscoveryNoSavedAccessTokensEnabled = () => {
    if (this.props.savedAccessTokensEnabled !== undefined &&
      this.props.savedAccessTokensEnabled === false) {
      this.setErrorState(AnalyticsExtensionsErrorCode.ED_TOKEN_ERROR);
      return;
    }
  };

  private handleUpdateAndCreateConnection = (connectionParams: ConnectionParams): Promise<boolean> => {
    const connectionType: string = this.convertScreenToType(connectionParams.typeIndex);
    this.handleTypeChangeCallback(connectionType, 'connection_type');
    let updateSettings = this.state.configSettings;
    let brief = this.state.configSettings.connection_brief;
    brief['connection_name'] = this.handleConnectionName(connectionType, connectionParams.connectionName);
    updateSettings['host'] = connectionParams.host;
    updateSettings['port'] = connectionParams.port;
    updateSettings['username'] = connectionParams.username;
    updateSettings['password'] = connectionParams.password;
    updateSettings['is_ssl_enabled'] = connectionParams.isSecure;
    updateSettings['is_auth_enabled'] = connectionParams.isAuthEnabled;
    updateSettings['connection_brief'] = brief;
    this.setState({ configSettings: updateSettings });
    this.updateProtocol();
    this.removeProtocolFromHostUrl();
    if (this.validateInput()) {
      this.setState({ busy: true });
      this.handleClearPrevError();
      this.handleCreate();
      return new Promise<boolean>((resolve, reject) => {
        if (this.state.error) {
          reject(this.state.error);
        } else {
          resolve(true);
        }
      });
    }
    // promise is rejected because dialog is in error state
    return Promise.reject();
  };

  private handleClearPrevError = () => {
    this.setState({
      error: null
    });
  };

  private hasEinsteinDiscoveryConnection = (): boolean => {
    const connectionList = this.props.ctrlProps.initialAnalyticsExtensionsSettings.connectionSettingsList;
    let einsteinDiscoveryConnectionOnSite: boolean = false;
    for (const connection of connectionList) {
      if (connection.connection_brief.connection_type === this.ConnectionTypes.EINSTEIN.DISPLAY) {
        einsteinDiscoveryConnectionOnSite = true;
        break;
      }
    }
    return einsteinDiscoveryConnectionOnSite;
  };

  private displayAlertWidget = () => {
    if (this.state.error === null) {
      return null;
    }
    return (
      <div>
        <InlineAlertWidget
          children={this.errorStrings[this.state.error]}
          testId={`anext-setting-dialog-error-alert-${this.state.error}`}
          type={InlineAlertType.Error}
        />
      </div>
    );
  };

  render() {
    const connectionParams = this.convertConnectionSettingToParams(this.state.configSettings);
    return (
      <section>
        {this.displayAlertWidget()}
        <div
          className={AnalyticsExtensionsSettingsStyles.editDialogSectionEDFFOn}>
          <AnalyticsExtensionsSettingsDialogComponent
            onClearContent={this.handleClearPrevError}
            onEinsteinDiscoveryDisable={() => {
            }}
            onSetConnection={(params: ConnectionParams): Promise<boolean> => {
              return this.handleUpdateAndCreateConnection(params);
            }}
            onCancel={this.props.dismiss}
            connectionParams={connectionParams}
            initialScreenType={this.convertTypeToScreen(
              this.state.configSettings.connection_brief.connection_type)}
            onEinsteinDiscoverySelectedWithNoSavedAccessTokensEnabled={() => {
              this.handleEinsteinDiscoveryNoSavedAccessTokensEnabled();
            }}
            savedAccessTokensEnabled={this.props.savedAccessTokensEnabled}
            isOnline={this.state.isOnline}
            isEDOnlineEnabled={this.state.isOnline}
            onEinsteinSelectedWithEinsteinDiscoveryConnectionAlreadyOnSite={() => {
              this.setErrorState(AnalyticsExtensionsErrorCode.ED_ALREADY_ADDED_ERROR);
            }}
            isEinsteinDiscoveryAlreadyEnabledOnSite={this.hasEinsteinDiscoveryConnection()}
          />
        </div>
      </section>
    );
  }
}
